/**
 * M6: Main Toolbar — bottom-center horizontal pill.
 *
 * Three button groups separated by vertical dividers:
 *   Group 1: Cinematic play/pause (M2 — fully wired)
 *   Group 2: Tools — Edit, Screenshot, Locate hotspot (stubs: M7/M8/M5/M9)
 *   Group 3: Navigation modes — Scissors / Pin / Target (M3 nav tools)
 *
 * Built on @heroui/react ButtonGroup.
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useViewerStore } from '@stores/viewerStore';
import { useAuthGuard } from '@hooks/useAuthGuard';
import { useRaceTrackStore } from '@/modules/race-track';
import { Button, ButtonGroup } from '@heroui/react';
import { MtTooltip } from '@components/MtTooltip';
import { IcPause, IcPlay, IcMeasure, IcScreenshot, IcNavFly, IcNavOrbit } from './ToolbarIcons';
import { Pill } from './PillPrimitives';
import { PillDividerV } from './PillDividers';
import { useAssetLibraryStore } from '@stores/assetLibraryStore';

// ---------------------------------------------------------------------------
// MainToolbar
// ---------------------------------------------------------------------------

interface MainToolbarProps {
  onToggleCinematic: () => void;
  className?: string;
}

export function MainToolbar({ onToggleCinematic, className }: MainToolbarProps) {
  const { t } = useTranslation();
  const { guard } = useAuthGuard();
  const cinematicState = useViewerStore((s) => s.cinematicState);
  const navMode = useViewerStore((s) => s.navigationMode);
  const setNavMode = useViewerStore((s) => s.setNavigationMode);
  const measureMode = useViewerStore((s) => s.measureMode);
  const snapshotBusy = useViewerStore((s) => s.snapshotBusy);
  const setMeasureMode = useViewerStore((s) => s.setMeasureMode);
  const requestSnapshot = useViewerStore((s) => s.requestSnapshot);
  const isEditing = useRaceTrackStore((s) => s.isEditing);
  const setEditing = useRaceTrackStore((s) => s.setEditing);
  const panelOpen = useAssetLibraryStore((s) => s.panelOpen);
  const togglePanel = useAssetLibraryStore((s) => s.togglePanel);

  const isPlaying = cinematicState === 'playing';
  const isMeasuring = measureMode !== 'idle';

  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <Pill horizontal className={`gap-2 px-1 ${className}`}>
      {/* Group 1: Cinematic — 无轨迹时隐藏 */}
      {cinematicState !== 'idle' && (
        <>
          <MtTooltip content={isPlaying ? t('toolbar.pauseCinematic') : t('toolbar.playCinematic')}>
            <Button
              isIconOnly
              aria-label={isPlaying ? t('toolbar.pauseCinematic') : t('toolbar.playCinematic')}
              onPress={onToggleCinematic}
              className="rounded-full"
              variant="secondary"
              size="md"
            >
              {isPlaying ? <IcPause className="size-4" /> : <IcPlay className="size-4" />}
            </Button>
          </MtTooltip>

          <PillDividerV />
        </>
      )}

      {/* Group 2: Tools — ButtonGroup managed */}
      <ButtonGroup className="gap-0" variant="secondary">
        <MtTooltip content={isMeasuring ? t('toolbar.exitMeasure') : t('toolbar.measureTool')}>
          <Button
            isIconOnly
            aria-label={isMeasuring ? t('toolbar.exitMeasure') : t('toolbar.measureTool')}
            onPress={() => guard(() => setMeasureMode(isMeasuring ? 'idle' : 'placing'))}
            className="rounded-l-full"
            variant={isMeasuring ? 'primary' : 'secondary'}
            size="md"
          >
            <IcMeasure className="size-4" />
          </Button>
        </MtTooltip>
        <MtTooltip content={t('toolbar.screenshot')}>
          <Button
            isIconOnly
            aria-label={t('toolbar.screenshot')}
            isDisabled={snapshotBusy}
            isPending={snapshotBusy}
            onPress={() => guard(() => requestSnapshot())}
            className={isMobile ? 'rounded-r-full' : 'rounded-none'}
            variant="secondary"
            size="md"
          >
            <ButtonGroup.Separator />
            <IcScreenshot className="size-4" />
          </Button>
        </MtTooltip>
        {!isMobile && (
          <MtTooltip content={t('toolbar.assetLibrary')}>
            <Button
              isIconOnly
              aria-label={t('toolbar.assetLibrary')}
              onPress={togglePanel}
              className="rounded-none"
              variant={panelOpen ? 'primary' : 'secondary'}
              size="md"
            >
              <ButtonGroup.Separator />
              <svg className="size-4" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M21 4.5v3.2a.3.3 0 01-.3.3H3.3a.3.3 0 01-.3-.3V4.5Q3 3 4.5 3h15Q21 3 21 4.5m-1 5.3V18q0 3-3 3H7q-3 0-3-3V9.8a.3.3 0 01.3-.3h15.4a.3.3 0 01.3.3M14.75 12c0-.41-.34-.75-.75-.75h-4c-.41 0-.75.34-.75.75s.34.75.75.75h4c.41 0 .75-.34.75-.75"
                />
              </svg>
            </Button>
          </MtTooltip>
        )}
        {!isMobile && (
          <MtTooltip content={t('toolbar.cinema')}>
            <Button
              isIconOnly
              aria-label={t('toolbar.cinema')}
              onPress={() => guard(() => setEditing(!isEditing))}
              className="rounded-r-full"
              variant={isEditing ? 'primary' : 'secondary'}
              size="md"
            >
              <ButtonGroup.Separator />
              <svg
                className="size-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="23 7 16 12 23 17 23 7" />
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
              </svg>
            </Button>
          </MtTooltip>
        )}
      </ButtonGroup>

      <PillDividerV />

      {/* Group 3: Navigation modes — ButtonGroup managed */}
      <ButtonGroup className="gap-0" variant="secondary" size="md">
        <MtTooltip content={t('toolbar.flyMode')} description={t('toolbar.flyDesc')}>
          <Button
            isIconOnly
            aria-label={t('toolbar.flyMode')}
            onPress={() => setNavMode('fly')}
            className="rounded-l-full"
            variant={navMode === 'fly' ? 'primary' : 'secondary'}
            size="md"
          >
            <IcNavFly className="size-4" />
          </Button>
        </MtTooltip>

        <MtTooltip content={t('toolbar.orbitMode')} description={t('toolbar.orbitDesc')}>
          <Button
            isIconOnly
            aria-label={t('toolbar.orbitMode')}
            onPress={() => setNavMode('orbit')}
            className="rounded-r-full"
            variant={navMode === 'orbit' ? 'primary' : 'secondary'}
            size="md"
          >
            <ButtonGroup.Separator />
            <IcNavOrbit className="size-4" />
          </Button>
        </MtTooltip>
      </ButtonGroup>
    </Pill>
  );
}
